import { PerformanceReviewEditSidebar } from "@/pages/talent/performance-reviews/edit/components/PerformanceReviewEditSidebar.tsx";
import {
  PerformanceReviewEditPageProps,
  usePerformanceReviewEditPage,
} from "@/pages/talent/performance-reviews/edit/pages/performance-review-edit-pages.tsx";
import { PerformanceReviewTitleSlot } from "@/pages/talent/performance-reviews/edit/pages/PerformanceReviewTitleSlot.tsx";
import { PerformanceReviewEditPageStep } from "@/pages/talent/performance-reviews/edit/performance-review-edit.ts";
import { PerformanceReviewReviewerTypeItems } from "@/types/performance-review.types.ts";
import { Box, Button, FullScreenPage, ListItem, Stack, Title } from "@skailer-com/uikit";
import { round } from "lodash";
import { useState } from "react";
import { useTranslation } from "react-i18next";

export const PerformanceReviewEditConfigureFormsPage = ({
  review,
}: PerformanceReviewEditPageProps) => {
  const { t } = useTranslation();
  const { confirmExit, navigateToStep, navigateToEditForm } = usePerformanceReviewEditPage();

  const [error, setError] = useState<string | null>(null);

  const onSubmit = () => {
    const hasMissingForm = review.reviewer_types.some((type) => !type.form);
    if (hasMissingForm) {
      setError(t("errors:forms_validation_error"));
      return;
    }

    setError(null);
    navigateToStep(review.id, PerformanceReviewEditPageStep.settings);
  };

  const referenceOrder = PerformanceReviewReviewerTypeItems().map((item) => item.id);
  const sortedReviewerTypes = review.reviewer_types.slice().sort((a, b) => {
    const indexA = referenceOrder.indexOf(a.type);
    const indexB = referenceOrder.indexOf(b.type);
    const safe = (i: number) => (i === -1 ? 999 : i);
    return safe(indexA) - safe(indexB);
  });

  return (
    <FullScreenPage
      backSlot={
        <Button variant={"secondary"} size={"lg"} onClick={() => confirmExit(review.id)}>
          {t("common:exit")}
        </Button>
      }
      actionsSlot={
        <>
          <Button
            variant={"secondary"}
            size={"lg"}
            onClick={() => {
              navigateToStep(review.id, PerformanceReviewEditPageStep.reviewers);
            }}
          >
            {t("common:back")}
          </Button>
          <Button size={"lg"} onClick={onSubmit}>
            {t("common:continue")}
          </Button>
        </>
      }
      titleSlot={
        <PerformanceReviewTitleSlot step={PerformanceReviewEditPageStep.forms} review={review} />
      }
      isCenterVertical={false}
      sidebarSlot={
        <PerformanceReviewEditSidebar step={PerformanceReviewEditPageStep.forms} review={review} />
      }
    >
      <Stack gap={"sm"}>
        <Stack className={"card-panel"} gap={"xl"}>
          <Title
            header={t("performance_review:choose_forms")}
            caption={t("performance_review:configure_form_description")}
            size={"lg"}
          />
        </Stack>
        {sortedReviewerTypes.map((type) => (
          <Stack
            key={`reviewer-item-${type.type}-${type.id ?? ""}`}
            className={"card-panel"}
            gap={"2xl"}
          >
            <Title
              header={
                PerformanceReviewReviewerTypeItems().find((x) => x.id == type.type)?.title ??
                type.title ??
                ""
              }
            />

            {type.form ? (
              <Stack direction={"horizontal"} items={"center"}>
                <ListItem
                  title={type.template_title ?? undefined}
                  caption={
                    t("plurals:question_count", { count: type.form.stats.total_questions }) +
                    ", " +
                    t("plurals:plural_minutes", {
                      count: round(type.form.stats.total_estimated_time_seconds / 60),
                    })
                  }
                  valueSlot={
                    <Button
                      variant={"secondary"}
                      onClick={() => {
                        navigateToEditForm(review.id, type.id);
                      }}
                    >
                      {t("common:edit")}
                    </Button>
                  }
                />
              </Stack>
            ) : (
              <Stack direction={"horizontal"}>
                <Button
                  variant={"primary"}
                  onClick={() => {
                    navigateToEditForm(review.id, type.id);
                  }}
                >
                  {t("performance_review:choose_form")}
                </Button>
              </Stack>
            )}
          </Stack>
        ))}
        {error && (
          <Box className="pt-2 pb-4">
            <span className="text-danger">{error}</span>
          </Box>
        )}
        <Button onClick={onSubmit} size={"lg"}>
          {t("common:continue")}
        </Button>
      </Stack>
    </FullScreenPage>
  );
};
